import { Component } from '@theme/component';

/**
 * Lightweight carousel for the Shop the Look section.
 *
 * On mobile + tablet the track is a horizontal scroll-snap slider (one card per
 * view, full container width); on desktop it falls back to a CSS grid. The dots
 * reflect the scrolled card and scroll to it on click.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The scrolling track holding the cards.
 * @property {HTMLButtonElement[]} [dots] - The pagination dots.
 *
 * @extends Component<Refs>
 */
export default class AwStlCarousel extends Component {
  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.track?.children ?? []));
  }

  /**
   * Scrolls the track to the slide at the given index.
   * @param {number} index
   */
  select(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    this.refs.track?.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#sync);
  };

  #sync = () => {
    const { track, dots } = this.refs;
    if (!track || !dots) return;

    const center = track.scrollLeft + track.clientWidth / 2;
    let active = 0;
    let closest = Infinity;

    this.#slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - center);
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });

    dots.forEach((dot, index) => dot.setAttribute('aria-selected', String(index === active)));
  };
}

if (!customElements.get('aw-stl-carousel')) {
  customElements.define('aw-stl-carousel', AwStlCarousel);
}
